/* eslint-disable @1js/office-online-rules/no-v8-imports-for-fluentui */
import { memoizeFunction } from "@fluentui/react";

import {
  getRibbonTextContainerSelector,
  ribbonDividerWrapperClassName,
} from "@1js/acui-common";

import {
  addInitialScalingStepsStyles,
  addMaxWidthSelectors,
  clearScaledGalleryCaches,
  estimateRibbonScalingStepsMLR,
  getClassNameCache,
  getCoreRibbonElementForGalleriesConvertedToFlyoutAnchorsSelector,
  getCoreRibbonElementSelector,
  getDropPreviewItemFromGalleryIndexSelector,
  getGalleriesConvertedToFlyoutByIdMode,
  getGhostFlyoutVisibleSelector,
  getRibbonBottomBarWidthMLR,
  getSelectorsForScalingSteps,
  initializeVirtualScalingStateMLR,
  refineScalingStepSelectors,
  setGalleriesConvertedToFlyoutByIdMode,
} from ".";
import type {
  BuildTimeControlWidths,
  EstimatedScalingWidthDict,
  RibbonScalingStateMLRWithCacheKeys,
  ScalingStepsStyle,
  Selectors,
  VirtualRibbonScalingStateMLR,
  ScalingClassNameProperties,
} from "..";
import {
  hiddenControlStyle,
  makeMeasurableControlVisibleStyle,
  makeControlVisibleStyle,
} from "..";
import { setEstimatedScalingWidths } from "../estimatedWidths";
import type {
  RibbonControlScalingTransformation,
  RibbonGroupScalingTransformation,
  RibbonScalingStep,
  RibbonScalingTransformation,
} from "../../../Scaling";
import {
  chevronWidth,
  tabContentContainerPadding,
} from "../RibbonBottomBarConstants";
import type { RibbonControlProps } from "../../..";
import {
  getDataLidSelector,
  hiddenMeasurableControlStyle,
  MLRControlGroupLabelId,
  MLRControlGroupSelector,
  vNextRibbonModeSwitcherLeftRightMargin,
  sectionDividerWidth,
} from "../../..";
import {
  groupFlyoutDataLid,
  initialDataLid,
} from "../../RibbonControlGroupMLR";
import type { AppContextType } from "@1js/acui-context";
import type { IStyle } from "@fluentui/style-utilities";

let estimatedWidths: EstimatedScalingWidthDict = {};
let lastKnownScalingState: RibbonScalingStateMLRWithCacheKeys | undefined =
  undefined;
let lastKnownMergedClassName: string | undefined = undefined;
let draftVirtualScalingState: VirtualRibbonScalingStateMLR | undefined =
  undefined;
let renderExpandedMLR = false;

/**
 * Gets a className that corresponds to the given scaling state
 * @param scalingProperties Properties used to compute the className
 * @returns A className for the given scaling state
 */
export function getScalingClassNameMLR(
  scalingProperties: ScalingClassNameProperties<RibbonScalingStateMLRWithCacheKeys>,
  mergeStyles: (...args: IStyle[]) => string // Passing this in as a param so this module doesn't need to take a hard dependency on fluent
) {
  const { scalingState, forceRecalc } = scalingProperties;

  // If we are not forced to recalc, we have known mergedClassName (we've calculated something before),
  // we can potentially return a cached className
  if (!forceRecalc && lastKnownMergedClassName) {
    if (
      lastKnownScalingState &&
      lastKnownScalingState.cacheKey === scalingState.cacheKey
    ) {
      return lastKnownMergedClassName;
    }

    // The scaling state is different from the previous call, but we may have already
    // computed the className and stored it in the cache. if so, return the cached className
    const cachedEntry = getClassNameCache().getCachedEntry(scalingState);
    if (cachedEntry) {
      lastKnownScalingState = scalingState;
      lastKnownMergedClassName = cachedEntry.className;
      estimatedWidths = cachedEntry.estimatedWidths;
      return lastKnownMergedClassName;
    }
  }

  const mergedStylesClassName = mergeStyles(
    getScalingStylesMLR(scalingProperties)
  );
  getClassNameCache().addMeasurementToCache(
    scalingState,
    mergedStylesClassName,
    estimatedWidths
  );
  lastKnownMergedClassName = mergedStylesClassName;

  return mergedStylesClassName;
}

/**
 * Gets a className that corresponds to the given scaling state
 * @param scalingProperties Properties used to compute the className
 * @returns A className for the given scaling state
 */
export function getScalingStylesMLR(
  scalingProperties: ScalingClassNameProperties<RibbonScalingStateMLRWithCacheKeys>
) {
  const {
    scalingState,
    knownWidths,
    reservedSpace,
    initialRender,
    appContext,
    modifiedEstimatedWidths,
  } = scalingProperties;

  // We have not computed the styling for this scaling state,
  // set up variables to build new styling
  lastKnownScalingState = scalingState;
  const style: ScalingStepsStyle = { selectors: {} };
  clearScaledGalleryCaches("Multiline");

  // Add the base styles that are always present
  addBaseStyles(style);

  // Add the styles for the initial control scaling steps
  addInitialScalingStepsStyles(
    scalingState,
    style,
    getScalingStepsStylesBase,
    appContext
  );

  // We only need to run the following steps if we are not in the initial render
  if (!initialRender) {
    if (modifiedEstimatedWidths) {
      estimatedWidths = modifiedEstimatedWidths;
    } else {
      // Update the estimated width of the ribbon for each scaling step
      updateEstimatedRibbonScalingStepWidths(
        scalingState,
        knownWidths,
        reservedSpace,
        appContext
      );
    }

    // Add the styles for the scaling steps. This will add the styles for the non-initial scaling steps
    addScalingStepsStyles(scalingState, style, appContext);
  }

  return style;
}

const containsConvertGroupToFlyout = (
  scalingSteps: Readonly<RibbonScalingStep>[] | Readonly<RibbonScalingStep>
): boolean => {
  if (Array.isArray(scalingSteps)) {
    return scalingSteps.some((scalingStep) =>
      containsConvertGroupToFlyout(scalingStep)
    );
  }

  return (scalingSteps as RibbonScalingTransformation).type === "GroupToFlyout";
};

/**
 * Adds the styles for scaling the ribbon controls for the scaling state
 * based off of the estimated widths
 * @param scalingState The scaling state to build the styles for
 * @param style The style object to write the new scaling styles
 */
const addScalingStepsStyles = (
  scalingState: RibbonScalingStateMLRWithCacheKeys,
  style: ScalingStepsStyle,
  appContext: AppContextType
) => {
  const layoutChangesPerGroup: { [groupId: string]: number } = {};

  // Loop over the estimated widths to get the max-widths.
  // The first value should be the initial width (after the initialScalingSteps applied)
  for (const key in estimatedWidths) {
    const scalingIndexFromWidth = +key;

    const scalingStep = scalingState.scalingSteps[
      scalingIndexFromWidth
    ] as RibbonGroupScalingTransformation;

    if (!scalingStep) {
      continue;
    }

    if (scalingStep.groupId && scalingStep.type === "ChangeGroupLayout") {
      layoutChangesPerGroup[scalingStep.groupId] = layoutChangesPerGroup[
        scalingStep.groupId
      ]
        ? layoutChangesPerGroup[scalingStep.groupId] + 1
        : 1;
    }

    // Add the selectors that make responsive scaling work
    addMaxWidthSelectors(
      style,
      estimatedWidths[key],
      scalingState,
      scalingIndexFromWidth,
      getScalingStepsStylesBase,
      appContext,
      layoutChangesPerGroup[scalingStep.groupId]
    );
  }
};

/**
 * Base function to build the styles for the given scaling state
 * @param scalingStepIndex The top level scaling state index
 * @param scalingState The scaling state that should be processed
 * @param layoutChangeCount The count of layout changes that have been applied for this group up to the scaling step index
 * @returns a styles object for (selectors and style attributes) for the scaling step (the scaling step from the given scaling state at the given index)
 */
const getScalingStepsStylesBase = (
  scalingStepIndex: number,
  scalingState: RibbonScalingStateMLRWithCacheKeys,
  appContext: AppContextType,
  layoutChangeCount: number = 0
) => {
  const selectors: Selectors = {};
  const scalingStep: RibbonScalingTransformation = scalingState.scalingSteps[
    scalingStepIndex
  ] as RibbonScalingTransformation;

  // If we don't get a scalingStep we cannot calculate the correct selectors, return early
  if (!scalingStep) {
    return selectors;
  }

  // Get core selector for the scaling step
  let controlsToHideSelector: string = getCoreScalingStepSelectorBase(
    scalingStep,
    appContext
  );

  let makeControlsVisibleSelector = undefined;
  let makeGroupFlyoutsVisibleSelector = undefined;

  // Check if we are a control scaling transformation
  if (!Object(scalingStep).hasOwnProperty("controlId")) {
    // If we are moving group to flyout, we need to use the groupId
    const groupId = (scalingStep as RibbonGroupScalingTransformation).groupId;

    if (scalingStep.type === "GroupToFlyout") {
      let controlsInGroupToHideSelector = undefined;

      // Build selectors for the GroupToFlyout scaling step
      ({ makeGroupFlyoutsVisibleSelector, controlsInGroupToHideSelector } =
        buildSelectorsForGroupToFlyout(scalingState, groupId));
      if (controlsInGroupToHideSelector) {
        controlsToHideSelector = controlsToHideSelector
          ? `${controlsToHideSelector}, ${controlsInGroupToHideSelector}`
          : controlsInGroupToHideSelector;
      }
    } else if (scalingStep.type === "ChangeGroupLayout") {
      let layoutToHideSelector = undefined;

      // Build selectors for the ChangeGroupLayout scaling step
      ({ makeControlsVisibleSelector, layoutToHideSelector } =
        getSelectorsForChangeGroupLayout(groupId, layoutChangeCount));
      controlsToHideSelector = controlsToHideSelector
        ? `${controlsToHideSelector}, ${layoutToHideSelector}`
        : layoutToHideSelector;
    }
  }

  // Get additional selectors for gallery to flyout, if needed
  const makeGhostFlyoutVisibleSelector =
    getGhostFlyoutVisibleSelector(scalingStep);

  if (controlsToHideSelector) {
    selectors[controlsToHideSelector] = hiddenControlStyle;
  }

  if (makeGhostFlyoutVisibleSelector) {
    selectors[makeGhostFlyoutVisibleSelector] =
      makeMeasurableControlVisibleStyle;
  }

  if (makeControlsVisibleSelector) {
    selectors[makeControlsVisibleSelector] = makeControlVisibleStyle;
  }

  if (makeGroupFlyoutsVisibleSelector) {
    selectors[makeGroupFlyoutsVisibleSelector] =
      makeMeasurableControlVisibleStyle;
  }

  return selectors;
};

/**
 * Returns a selector based on the scaling index, the scaling steps, and the overall scaling index
 * @param scalingSteps The scaling step to build the selector from
 * @returns a selector for the given scaling step. This determines control and label visibility
 */
const getCoreScalingStepSelector = (
  scalingStep: Readonly<RibbonScalingStep>,
  appContext: AppContextType
): string => {
  if (Array.isArray(scalingStep)) {
    // Build a selector comprised of all scaling steps in the array since they will all be scaled at the same time
    return getSelectorsForScalingSteps(
      scalingStep,
      getCoreScalingStepSelectorBase,
      appContext
    );
  }

  return getSelectorForScalingStepBasedOnType(scalingStep);
};

/**
 * Memoized - Returns a selector based on the scaling index, the scaling steps, and the overall scaling index
 * @param scalingSteps The scaling step to build the selector from
 * @returns a selector for the given scaling step. This determines control and label visibility
 */
const getCoreScalingStepSelectorMemo = memoizeFunction(
  (
    scalingStep: Readonly<RibbonScalingStep>,
    appContext: AppContextType
  ): string => {
    return getCoreScalingStepSelector(scalingStep, appContext);
  }
);

/**
 * Returns a selector based on the scaling index, the scaling steps, and the overall scaling index
 * @param scalingSteps The scaling step to build the selector from
 * @returns a selector for the given scaling step. This determines control and label visibility
 */
const getCoreScalingStepSelectorBase = (
  scalingStep: Readonly<RibbonScalingStep> | undefined,
  appContext: AppContextType
): string => {
  if (!scalingStep) {
    return "";
  }

  // Do not memoize the DropPreviewItemFromGallery scaling step due to intermittent scaling issues
  const scalingStepSelectorFn =
    (scalingStep as RibbonScalingTransformation).type ===
    "DropPreviewItemFromGallery"
      ? getCoreScalingStepSelector
      : getCoreScalingStepSelectorMemo;

  return scalingStepSelectorFn(scalingStep, appContext);
};

/**
 * Adds the base styles that are present for all scaling steps
 * @param style The style object to add the base styles
 */
function addBaseStyles(style: ScalingStepsStyle) {
  // Initially hide all layout ids (they will be shown at the correct time)
  style.selectors[getDataLidSelector()] = hiddenControlStyle;

  // Initially hide groupFlyout layout ids (data-lid="-1") in a way that they can be measured
  style.selectors[getDataLidSelector(groupFlyoutDataLid)] =
    hiddenMeasurableControlStyle;

  // Show the initial layout id (data-lid="0")
  style.selectors[getDataLidSelector(initialDataLid)] = makeControlVisibleStyle;
}

/**
 * Build selectors for the ChangeGroupLayout of the given group id
 * @param groupId group id to build the selector for
 * @param layoutChangeCount The number of layout changes that have been applied
 * @returns The selectors for applying the ChangeGroupLayout scaling step
 */
function getSelectorsForChangeGroupLayout(
  groupId: string,
  layoutChangeCount: number
) {
  const groupIdSelector = MLRControlGroupSelector(groupId);

  // Show the layout id (data-lid) for this group
  const makeControlsVisibleSelector = `${groupIdSelector} ${getDataLidSelector(
    layoutChangeCount
  )}`;

  // Hide the pervious layout id (data-lid) for this group
  const layoutToHideSelector = `${groupIdSelector} ${getDataLidSelector(
    layoutChangeCount - 1
  )}`;

  return { makeControlsVisibleSelector, layoutToHideSelector };
}

/**
 * Build selectors for the GroupToFlyout of the given group id
 * @param scalingState The ribbon scaling state
 * @param groupId The id of the group to build the selector for
 * @returns The selectors for applying the GroupToFlyout scaling step
 */
function buildSelectorsForGroupToFlyout(
  scalingState: RibbonScalingStateMLRWithCacheKeys,
  groupId: string
) {
  const groupSelector = MLRControlGroupSelector(groupId);
  const makeGroupFlyoutsVisibleSelector = `${groupSelector} ${getDataLidSelector(
    groupFlyoutDataLid
  )}`;

  // This hides all the controls in the group since the flyout is going to be shown
  const scaledGroup = scalingState.scaledRibbonGroups.find(
    (group) => group.id === groupId
  );

  let controlsInGroupToHideSelector = undefined;

  // Add the selector for the controls in this group to the overall selector being built,
  // not including the GroupToFlyout control that is the last control in the list of controls for the scaled group
  if (scaledGroup?.controls) {
    const controlsSelectors = getSelectorsForControls(
      scaledGroup.controls.slice(0, scaledGroup.controls.length - 1)
    );

    const labelIdSelector = `#${MLRControlGroupLabelId(groupId)}`;

    // Make sure we have controls selectors, if not just use the group label.
    // Also, make sure the divider is hidden.
    controlsInGroupToHideSelector = `${
      controlsSelectors ? `${controlsSelectors}, ` : ""
    }${labelIdSelector}, ${groupSelector} .${ribbonDividerWrapperClassName}`;
  }

  return { makeGroupFlyoutsVisibleSelector, controlsInGroupToHideSelector };
}

/**
 * Get a selector comprised of all scaling steps in the array
 * @param scalingSteps The scaling step to build the selector from
 * @param topLevelScalingIndex The scaling index in the top level scaling steps
 * @param moveToOverflowFullClassNameSelector The selector for the moveToOverflow scaling step
 * @returns A selector for the given scaling step array
 */
function getSelectorsForControls(controls: RibbonControlProps[]) {
  // Combine the selectors for each step in the array
  const selectors: string = controls.reduce(
    (resultingSelectors, control) =>
      `${resultingSelectors}, ${getCoreRibbonElementSelector(
        control.id,
        undefined /* suffix */
      )}`,
    ""
  );

  return refineScalingStepSelectors(selectors);
}

/**
 * Gets a selector for the element in the given scaling step based on transformation type
 * @param scalingStep The scaling step used to build the selector
 * @returns A selector for the given scaling step or an empty string if do not know the scaling transformation
 */
function getSelectorForScalingStepBasedOnType(
  scalingStep: Readonly<RibbonScalingStep>
) {
  const id =
    (scalingStep as RibbonControlScalingTransformation).controlId ||
    (scalingStep as RibbonGroupScalingTransformation).groupId;

  const elementSelector = getGalleriesConvertedToFlyoutByIdMode(id, "Multiline")
    ? getCoreRibbonElementForGalleriesConvertedToFlyoutAnchorsSelector(id)
    : getCoreRibbonElementSelector(id, undefined /* suffix */);

  // Build the selector for the given controlId for the give scaling step
  switch ((scalingStep as RibbonScalingTransformation).type) {
    case "DropLabel":
      return `${elementSelector} ${getRibbonTextContainerSelector(
        true /*useOptimizedSelector*/
      )}`;
    case "DropPreviewItemFromGallery":
      return `${elementSelector} ${getDropPreviewItemFromGalleryIndexSelector(
        id,
        "Multiline"
      )}`;
    case "GalleryToFlyout":
      setGalleriesConvertedToFlyoutByIdMode(
        id,
        "Multiline",
        true /* isConvertedToFlyout */
      );
      return elementSelector;
    default:
      return "";
  }
}

/**
 * Update the estimated ribbon widths based on the provided scaling steps. This function also
 * adds new control, previously unknown, widths to known widths
 * @param scalingState The scaling state to used to calculate the estimated ribbon widths per scaling step
 * @param knownWidths The known control widths
 * @param reservedSpace How much space is reserved for non-Ribbon elements
 */
function updateEstimatedRibbonScalingStepWidths(
  scalingState: RibbonScalingStateMLRWithCacheKeys,
  knownWidths: BuildTimeControlWidths,
  reservedSpace: number,
  appContext: AppContextType
) {
  // Initialize the virtualScalingState and fill it with our known widths
  draftVirtualScalingState = initializeVirtualScalingStateMLR(
    scalingState,
    knownWidths
  );

  const scalingData = getRibbonBottomBarWidthMLR(
    scalingState,
    draftVirtualScalingState,
    // Account for bottomBar padding, the modeSwitcher, and add a section divider width
    // buffer (without the buffer, the last divider could be cut off intermittently)
    // Next styles adds 4px of margin on each side of ribbon mode button
    tabContentContainerPadding +
      vNextRibbonModeSwitcherLeftRightMargin * 2 +
      chevronWidth +
      sectionDividerWidth +
      reservedSpace
  );

  // Determine the effect of applying each scaling step
  estimatedWidths = estimateRibbonScalingStepsMLR(
    scalingState,
    renderExpandedMLR ? 0 : scalingData.width,
    appContext,
    draftVirtualScalingState
  );

  setEstimatedScalingWidths(estimatedWidths);
}

export const setRenderExpandedMLR = (value: boolean) => {
  renderExpandedMLR = value;
};
